/**
 * viewportAnalysisCore.js — чистые части из core/viewportAnalysis.js.
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 FIXES]:
 *  - agatstonPerLesion → agatstonPerSlice (честное название: 2D-кластеризация)
 *  - agatston3D: НАСТОЯЩИЙ по-очаговый Агатстон с 3D-связностью (6-связность)
 *  - huMax: отсечение кости (>1000 HU) для кальциевого скоринга
 *  - detectBodyRegion: защита от пустого массива
 *  - classifyTissue: lookup-таблица на 4096 элементов для O(1)
 *  - TISSUE_TABLE: экспортируется напрямую
 *
 * Перенесено 1:1 (сверено с оригиналом): таблица тканей + classifyTissue,
 * computeTissueDensityMap, _detectBodyRegion и ДВА варианта Агатстона:
 *   - agatstonPerSlice: 2D BFS 4-связность + нормировка толщины /3мм (быстрый)
 *   - agatston3D: 3D BFS 6-связность + нормировка (клинически точный)
 * Браузер/рендер-движок убраны — они к математике отношения не имеют.
 */

// ══════════════════════════════════════════════════════════════
//  TISSUE TABLE + FAST LOOKUP
// ══════════════════════════════════════════════════════════════

export const TISSUE_TABLE = Object.freeze([
  { min: -1100, max: -900, name: 'Воздух / Лёгкие',   nameEn: 'Air/Lung',       color: '#1a1a2e' },
  { min: -900,  max: -500, name: 'Жир / Эмфизема',    nameEn: 'Fat/Emphysema',  color: '#e6b800' },
  { min: -500,  max: -100, name: 'Жировая ткань',     nameEn: 'Adipose',        color: '#ffdd57' },
  { min: -100,  max: -30,  name: 'Жир',               nameEn: 'Fat',            color: '#f0c040' },
  { min: -30,   max: 20,   name: 'Вода / Выпот',      nameEn: 'Water/Effusion', color: '#4ecdc4' },
  { min: 20,    max: 60,   name: 'Мягкая ткань',      nameEn: 'Soft tissue',    color: '#c0392b' },
  { min: 60,    max: 90,   name: 'Кровь / Мышца',     nameEn: 'Blood/Muscle',   color: '#e74c3c' },
  { min: 90,    max: 200,  name: 'Контраст / Кровь',  nameEn: 'Contrast/Blood', color: '#e91e63' },
  { min: 200,   max: 700,  name: 'Кальцинат',         nameEn: 'Calcification',  color: '#ecf0f1' },
  { min: 700,   max: 3000, name: 'Кость',             nameEn: 'Bone',           color: '#f5f5f5' },
  { min: 3000,  max: 4096, name: 'Металл',            nameEn: 'Metal',          color: '#ffffff' },
]);

// Lookup-таблица: HU → индекс в TISSUE_TABLE (O(1) вместо O(n))
const _TISSUE_LOOKUP = new Int8Array(4096).fill(-1);
(function _buildLookup() {
  const offset = 1024; // HU -1024 → индекс 0
  for (let i = 0; i < TISSUE_TABLE.length; i++) {
    const t = TISSUE_TABLE[i];
    const lo = Math.max(0, t.min + offset);
    const hi = Math.min(4095, t.max + offset);
    for (let hu = lo; hu <= hi; hu++) {
      _TISSUE_LOOKUP[hu] = i;
    }
  }
})();

const UNKNOWN_TISSUE = Object.freeze({
  min: -Infinity, max: Infinity, name: 'Неизвестно', nameEn: 'Unknown', color: '#888',
});

/**
 * Классификация HU → ткань. O(1) через lookup-таблицу, O(n) fallback.
 * @param {number} hu — значение в единицах Хаунсфилда
 * @returns {Object} { min, max, name, nameEn, color }
 */
export function classifyTissue(hu) {
  const idx = Math.round(hu) + 1024;
  if (idx >= 0 && idx < 4096) {
    const tissueIdx = _TISSUE_LOOKUP[idx];
    if (tissueIdx >= 0) return TISSUE_TABLE[tissueIdx];
  }
  // Fallback: линейный поиск для значений вне диапазона
  for (const t of TISSUE_TABLE) {
    if (hu >= t.min && hu < t.max) return t;
  }
  return UNKNOWN_TISSUE;
}

/** Получить копию таблицы тканей (для UI) */
export function getTissueTable() { return TISSUE_TABLE.map(t => ({ ...t })); }

// ══════════════════════════════════════════════════════════════
//  TISSUE DENSITY MAP
// ══════════════════════════════════════════════════════════════

/**
 * Подсчитать процентное соотношение типов тканей в наборе HU-значений.
 * @param {number[]} huValues
 * @returns {Object[]} [{ tissue, count, percent }]
 */
export function computeTissueDensityMap(huValues) {
  if (!huValues?.length) return [];
  const counts = new Map();
  let validCount = 0;
  for (const hu of huValues) {
    if (isNaN(hu)) continue;
    const t = classifyTissue(hu);
    counts.set(t.name, (counts.get(t.name) || 0) + 1);
    validCount++;
  }
  if (validCount === 0) return [];
  return [...counts.entries()]
    .map(([tissue, count]) => ({
      tissue,
      count,
      percent: +((count / validCount) * 100).toFixed(1),
    }))
    .sort((a, b) => b.percent - a.percent);
}

// ══════════════════════════════════════════════════════════════
//  BODY REGION DETECTOR
// ══════════════════════════════════════════════════════════════

/**
 * Определить анатомическую область по распределению HU.
 * @param {number[]} sortedValues — ОТСОРТИРОВАННЫЙ массив HU-значений
 * @returns {string}
 */
export function detectBodyRegion(sortedValues) {
  const n = sortedValues.length;
  if (!n) return 'Неизвестно (нет данных)';

  const p25 = sortedValues[Math.floor(n * 0.25)];
  const p75 = sortedValues[Math.floor(n * 0.75)];
  const median = sortedValues[Math.floor(n * 0.5)];

  if (median < -400) return 'Грудная клетка (лёгкие)';
  if (p25 > 10 && p75 < 80 && median > 20 && median < 50) return 'Головной мозг';
  if (median > -20 && median < 60 && p75 < 200) return 'Брюшная полость';
  if (p75 > 300) return 'MSK / Кости';
  if (p75 > 150 && median > 40) return 'Ангиография';
  return 'Общее';
}

// ══════════════════════════════════════════════════════════════
//  AGATSTON CALCIUM SCORING
// ══════════════════════════════════════════════════════════════

/**
 * Агатстон: посрезовый (2D BFS, 4-связность). Быстрый, но менее точный.
 *
 * Для клинической точности используйте agatston3D (3D-связность).
 *
 * @param {Float32Array|Int16Array} sd — скалярные данные объёма
 * @param {number[]} dims — [cols, rows, slices]
 * @param {number[]} spacing — [dx, dy, dz] в мм
 * @param {number} [threshold=130] — нижний порог HU
 * @param {number} [huMax=1000] — верхний порог HU (отсечение кости)
 * @param {number} [minAreaMm2=1] — минимальная площадь очага
 * @returns {number} totalScore (округлённый)
 */
export function agatstonPerSlice(sd, dims, spacing, threshold = 130, huMax = 1000, minAreaMm2 = 1) {
  const [cols, rows, slices] = dims;
  const pixelAreaMm2 = spacing[0] * spacing[1];
  const thicknessNorm = spacing[2] / 3.0;
  let totalScore = 0;

  for (let z = 0; z < slices; z++) {
    const off = z * rows * cols;
    const visited = new Uint8Array(rows * cols);
    let sliceScore = 0;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const idx = y * cols + x;
        const hu = sd[off + idx];
        if (visited[idx] || hu < threshold || hu > huMax) continue;

        visited[idx] = 1;
        const queue = [idx];
        let size = 0, peakHU = hu, head = 0;

        while (head < queue.length) {
          const ci = queue[head++];
          size++;
          const cv = sd[off + ci];
          if (cv > peakHU) peakHU = cv;

          const cx = ci % cols;
          const cy = (ci / cols) | 0;
          const neighbors = [
            cx > 0 ? ci - 1 : -1,
            cx < cols - 1 ? ci + 1 : -1,
            cy > 0 ? ci - cols : -1,
            cy < rows - 1 ? ci + cols : -1,
          ];

          for (const ni of neighbors) {
            if (ni >= 0 && !visited[ni]) {
              const nhu = sd[off + ni];
              if (nhu >= threshold && nhu <= huMax) {
                visited[ni] = 1;
                queue.push(ni);
              }
            }
          }
        }

        const area = size * pixelAreaMm2;
        if (area < minAreaMm2) continue;

        const weight = peakHU >= 400 ? 4 : peakHU >= 300 ? 3 : peakHU >= 200 ? 2 : 1;
        sliceScore += area * weight * thicknessNorm;
      }
    }

    totalScore += Math.round(sliceScore);
  }

  return totalScore;
}

// ══════════════════════════════════════════════════════════════
//  AGATSTON 3D — ПО-ОЧАГОВЫЙ (клинически точный)
// ══════════════════════════════════════════════════════════════

/**
 * По-очаговый Агатстон с 3D-связностью (6-связность).
 *
 * Алгоритм:
 *   1. 3D-связные компоненты (BFS 6-связность) — очаги
 *   2. Для каждого очага на каждом срезе: площадь × вес по пиковому HU
 *   3. Нормировка толщины среза / 3 мм
 *   4. Суммирование
 *
 * @param {Float32Array|Int16Array} sd — скалярные данные объёма
 * @param {number[]} dims — [cols, rows, slices]
 * @param {number[]} spacing — [dx, dy, dz] в мм
 * @param {number} [threshold=130] — нижний порог HU
 * @param {number} [huMax=1000] — верхний порог HU (отсечение кости)
 * @param {number} [minVoxels=3] — минимальный размер очага (вокселей)
 * @returns {{ totalScore: number, lesionCount: number, perSlice: Float32Array }}
 */
export function agatston3D(sd, dims, spacing, threshold = 130, huMax = 1000, minVoxels = 3) {
  const [cols, rows, slices] = dims;
  const voxelAreaMm2 = spacing[0] * spacing[1];
  const thicknessNorm = spacing[2] / 3.0;
  const sliceSize = cols * rows;
  const totalVoxels = sliceSize * slices;

  // ── Шаг 1: маска кальция ──
  const mask = new Uint8Array(totalVoxels);
  for (let i = 0; i < totalVoxels; i++) {
    const hu = sd[i];
    mask[i] = (hu >= threshold && hu <= huMax) ? 1 : 0;
  }

  // ── Шаг 2: 3D-связные компоненты (6-связность, BFS) ──
  const labels = new Int32Array(totalVoxels);
  const queue = new Int32Array(totalVoxels);
  let nextLabel = 1;

  const idx3D = (x, y, z) => z * sliceSize + y * cols + x;

  for (let z = 0; z < slices; z++) {
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const i = idx3D(x, y, z);
        if (!mask[i] || labels[i]) continue;

        labels[i] = nextLabel;
        let head = 0, tail = 0;
        queue[tail++] = i;

        while (head < tail) {
          const ci = queue[head++];
          const cz = Math.floor(ci / sliceSize);
          const cy = Math.floor((ci % sliceSize) / cols);
          const cx = ci % cols;

          const neighbors = [
            cx > 0 ? idx3D(cx - 1, cy, cz) : -1,
            cx < cols - 1 ? idx3D(cx + 1, cy, cz) : -1,
            cy > 0 ? idx3D(cx, cy - 1, cz) : -1,
            cy < rows - 1 ? idx3D(cx, cy + 1, cz) : -1,
            cz > 0 ? idx3D(cx, cy, cz - 1) : -1,
            cz < slices - 1 ? idx3D(cx, cy, cz + 1) : -1,
          ];

          for (const ni of neighbors) {
            if (ni >= 0 && mask[ni] && !labels[ni]) {
              labels[ni] = nextLabel;
              queue[tail++] = ni;
            }
          }
        }
        nextLabel++;
      }
    }
  }

  const lesionCount = nextLabel - 1;
  if (lesionCount === 0) {
    return { totalScore: 0, lesionCount: 0, perSlice: new Float32Array(slices) };
  }

  // ── Шаг 3: статистика по очагам ──
  // compStats[label] = { voxelCount, zMap: Map<z, {voxels, maxHU}> }
  const compStats = {};
  for (let i = 1; i <= lesionCount; i++) {
    compStats[i] = { voxelCount: 0, zMap: new Map() };
  }

  for (let i = 0; i < totalVoxels; i++) {
    const lbl = labels[i];
    if (!lbl) continue;
    const st = compStats[lbl];
    st.voxelCount++;
    const z = Math.floor(i / sliceSize);
    let zd = st.zMap.get(z);
    if (!zd) { zd = { voxels: 0, maxHU: 0 }; st.zMap.set(z, zd); }
    zd.voxels++;
    const hu = sd[i];
    if (hu > zd.maxHU) zd.maxHU = hu;
  }

  // ── Шаг 4: подсчёт счёта ──
  let totalScore = 0;
  const perSlice = new Float32Array(slices);

  for (let i = 1; i <= lesionCount; i++) {
    const st = compStats[i];
    if (st.voxelCount < minVoxels) continue;

    for (const [z, zd] of st.zMap) {
      const weight = zd.maxHU >= 400 ? 4 : zd.maxHU >= 300 ? 3 : zd.maxHU >= 200 ? 2 : 1;
      const score = zd.voxels * voxelAreaMm2 * weight * thicknessNorm;
      totalScore += Math.round(score);
      perSlice[z] += score;
    }
  }

  return {
    totalScore: Math.round(totalScore),
    lesionCount,
    perSlice,
  };
}

export default {
  classifyTissue, getTissueTable, computeTissueDensityMap,
  detectBodyRegion, agatstonPerSlice, agatston3D, TISSUE_TABLE,
};